import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useSnackbar } from "notistack";
import { useEmailAiSettings, useUpdateEmailAiSettings } from "../../hooks/useEmail";

// The AI follow-up switch for ONE mailbox (the one selected in the user switcher). Every email leaves from a staff
// member's own Gmail, so the AI replies from that person's address — which is why turning it on asks first, and why
// only admins can change it. Everyone else just sees whether it is on for their own mailbox. Renders nothing when the
// settings can't be read (another person's mailbox for a non-admin, or "All users").
export default function EmailAiControl({ userId, userLabel }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useEmailAiSettings(userId);
  const updateMutation = useUpdateEmailAiSettings();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!data?.settings) return null;
  const { settings, canEdit } = data;

  const save = (payload, { onDone, successMessage = "AI settings saved" } = {}) =>
    updateMutation.mutate(
      { userId, ...payload },
      {
        onSuccess: () => {
          enqueueSnackbar(successMessage, { variant: "success" });
          onDone?.();
        },
        onError: (error) => enqueueSnackbar(error?.response?.data?.message || "Could not save AI settings", { variant: "error" }),
      }
    );

  if (!canEdit) {
    return (
      <Tooltip title="Whether the AI assistant answers creators' email replies from your mailbox. Ask an admin to change it.">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <SmartToyOutlinedIcon sx={{ fontSize: 17, color: settings.enabled ? "#09AC31" : "#9CA3AF" }} />
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 12.5, fontWeight: 700, color: settings.enabled ? "#09AC31" : "#6B7280" }}>
            AI assistant {settings.enabled ? "on" : "off"}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="AI assistant: answers creators' email replies on plan emails sent from this mailbox and collects their rates and details">
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <SmartToyOutlinedIcon sx={{ fontSize: 17, color: settings.enabled ? "#09AC31" : "#9CA3AF" }} />
          <Typography sx={{ fontFamily: "Quicksand", fontSize: 12.5, fontWeight: 700, color: "#374151", ml: 0.5 }}>AI assistant</Typography>
          <Switch
            size="small"
            checked={Boolean(settings.enabled)}
            disabled={updateMutation.isLoading}
            onChange={(e) => (e.target.checked ? setConfirmOpen(true) : save({ enabled: false }, { successMessage: "AI assistant turned off" }))}
          />
          <IconButton size="small" onClick={() => setSettingsOpen(true)} aria-label="AI assistant settings">
            <SettingsOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Stack>
      </Tooltip>

      {/* Turning it on sends automatic emails from a real person's address — say so before it happens. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "Quicksand", fontWeight: 800 }}>Turn on the AI assistant?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: "#374151" }}>
            When a creator replies to a plan email sent from <b>{userLabel || "this mailbox"}</b>, the assistant will email them back from that
            same address and ask for any missing rates, number, city, state, category and language. Their answers are saved to the plan and the creator record.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#6B7280", mt: 1.5 }}>
            It handles the whole conversation itself, negotiation included: it records whatever rate the creator quotes and never counters. It ignores automatic replies, stops for anyone who asks not to be emailed or isn't interested, and stays quiet while a teammate is replying.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={updateMutation.isLoading}
            onClick={() => save({ enabled: true }, { onDone: () => setConfirmOpen(false), successMessage: "AI assistant turned on" })}
            sx={{ textTransform: "none" }}
          >
            Turn on
          </Button>
        </DialogActions>
      </Dialog>

      {settingsOpen && (
        <SettingsDialog
          // Re-mounted each time it opens so the fields start from the saved values.
          key={String(settingsOpen)}
          settings={settings}
          userLabel={userLabel}
          saving={updateMutation.isLoading}
          onClose={() => setSettingsOpen(false)}
          onSave={(payload) => save(payload, { onDone: () => setSettingsOpen(false) })}
        />
      )}
    </>
  );
}

function SettingsDialog({ settings, userLabel, saving, onClose, onSave }) {
  const [systemPrompt, setSystemPrompt] = useState(settings.systemPrompt || "");
  const [signOff, setSignOff] = useState(settings.signOff ?? "");
  const [perDay, setPerDay] = useState(String(settings.maxRepliesPerDay));
  const [perThread, setPerThread] = useState(String(settings.maxRepliesPerThread));
  const inRange = (v, max) => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= max;
  const valid = inRange(perDay, 50) && inRange(perThread, 100);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: "Quicksand", fontWeight: 800 }}>AI assistant — {userLabel || "this mailbox"}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
          AI emails are sent from this mailbox's own address, only on threads started from a plan email. The closing line below is added to the
          end of every AI email — leave it blank for none, or use it to say the message was written by an assistant.
        </Alert>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          label="Extra instructions (optional)"
          placeholder="e.g. Keep replies under 3 lines. Mention we usually reply within 2 working days."
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          inputProps={{ maxLength: 2000 }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          maxRows={3}
          label="Closing line"
          placeholder="Idiotic Media team"
          value={signOff}
          onChange={(e) => setSignOff(e.target.value)}
          inputProps={{ maxLength: 200 }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <TextField
            label="Max AI emails per thread per day"
            value={perDay}
            onChange={(e) => setPerDay(e.target.value)}
            error={!inRange(perDay, 50)}
            helperText={inRange(perDay, 50) ? " " : "1 to 50"}
            size="small"
            sx={{ width: 240 }}
          />
          <TextField
            label="Max AI emails per thread in total"
            value={perThread}
            onChange={(e) => setPerThread(e.target.value)}
            error={!inRange(perThread, 100)}
            helperText={inRange(perThread, 100) ? "Safety caps — the AI stops replying once reached." : "1 to 100"}
            size="small"
            sx={{ width: 260 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || saving}
          onClick={() => onSave({ systemPrompt, signOff, maxRepliesPerDay: Number(perDay), maxRepliesPerThread: Number(perThread) })}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
